import { useMemo, useState } from 'react';
import { Button, FormAutoGenerated, FormLoadingBox, Wrapper } from '@visualfabriq/vf-ui-kit';
import {
  FieldItem,
  FormLayoutRow,
} from '@visualfabriq/vf-ui-kit/dist/components/molecules/form/form-auto-generated/types';

import { FileMoverStep } from 'src/dto/PipelineStep';
import { FilesRoot } from 'src/dto/files/FilesRoots';
import { StepConfigLayout } from '../StepConfigLayout';
import { requiredField } from 'src/components/UIKitTweaks';

type Props = {
  step: FileMoverStep;
  defaultFieldsConfiguration: JSX.Element;
  loading: boolean;
  onUpdate: (step: FileMoverStep) => void;
  deleteButton: JSX.Element;
};

export function MoveFilesStepConfig(props: Props) {
  const { defaultFieldsConfiguration, onUpdate, step, loading, deleteButton } = props;

  const initialData = useMemo(() => {
    const { fnmatch_expression, from_folder, to_folder, backup_to_subfolder } = step.configuration;
    return {
      from_bucket: FilesRoot.ExportQueue,
      to_bucket: FilesRoot.ExportProcessed,
      fnmatch_expression: fnmatch_expression ?? '*.*',
      from_folder: from_folder ?? '',
      to_folder: to_folder ?? '',
      backup_to_subfolder: backup_to_subfolder ?? false,
    };
  }, [step]);
  const [formState, setFormState] = useState(initialData);

  const handleUpdate = () => {
    const { to_folder, from_folder, fnmatch_expression, backup_to_subfolder } = formState;
    onUpdate({
      ...step,
      configuration: {
        ...step.configuration,
        to_folder: to_folder === '' ? null : to_folder,
        from_folder: from_folder === '' ? null : from_folder,
        fnmatch_expression,
        backup_to_subfolder,
      },
    });
  };

  return (
    <StepConfigLayout
      loader={<FormLoadingBox items={['text', 'text', 'text', 'text', 'text']} itemsCount={5} />}
      loading={loading}
      footer={
        <Wrapper gap="200" direction="column">
          <Button onClick={handleUpdate}>Update Step</Button>
          {deleteButton}
        </Wrapper>
      }
    >
      {defaultFieldsConfiguration}
      <FormAutoGenerated
        initData={initialData}
        fields={fields}
        layout={layout}
        onDataChange={(event) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setFormState(event as any);
        }}
      />
    </StepConfigLayout>
  );
}

const fields: FieldItem[] = [
  {
    field_id: 'fnmatch_expression',
    field_desc: 'Filename Pattern',
    data_type: 'text',
    ...requiredField,
  },
  {
    field_id: 'from_bucket',
    field_desc: 'From Bucket',
    data_type: 'text',
    ...requiredField,
    is_editable_field: false,
  },
  {
    field_id: 'from_folder',
    field_desc: 'From Folder',
    data_type: 'text',
  },
  {
    field_id: 'to_bucket',
    field_desc: 'To Bucket',
    data_type: 'text',
    ...requiredField,
    is_editable_field: false,
  },
  {
    field_id: 'to_folder',
    field_desc: 'To Folder',
    data_type: 'text',
  },
  {
    field_id: 'backup_to_subfolder',
    field_desc: 'Backup to Subfolder',
    data_type: 'boolean',
  },
];

const layout: FormLayoutRow[] = [
  [{ field: 'fnmatch_expression', cols: [4, 6, 12] }],
  [{ field: 'from_bucket', cols: [4, 6, 12] }],
  [{ field: 'from_folder', cols: [4, 6, 12] }],
  [{ field: 'to_bucket', cols: [4, 6, 12] }],
  [{ field: 'to_folder', cols: [4, 6, 12] }],
  [{ field: 'backup_to_subfolder', cols: [4, 6, 12] }],
];
